import type { PoiIconName } from './poiIcons.js';

// Every leaf names a bundled icon, so a mistyped or removed one is a build
// error instead of a silently missing icon. Structurally a `Node`, which is
// what the generic tag resolver takes.
interface IconNode {
  [key: string]: IconNode | PoiIconName;
}

// Also used on its own under `amenity=place_of_worship`, where the `*` fallback
// below must not apply: `building=yes` says nothing there, and a reading of it
// would keep the amenity's own fallback from ever firing.
const worshipBuildingMapping: IconNode = {
  church: 'church',
  cathedral: 'church',
  temple: 'church',
  chapel: 'chapel',
  mosque: 'mosque',
  synagogue: 'synagogue',
};

// hack to have only single icon for churches...
const buildingMapping: IconNode = {
  '*': 'building',
  ...worshipBuildingMapping,
  hut: 'hut',
  farm: 'farm',
  parking: 'parking',
  warehouse: 'warehouse',
};

// A tower is named by its type where it has a distinctive one, and by its
// construction otherwise. Each type repeats the construction key so its reading
// carries both tags — the resolver then drops the construction-only reading as
// the more generic one, instead of letting the tag order decide.
const towerOfType = (type: PoiIconName): IconNode => ({
  '*': type,
  'tower:construction': { '*': type },
});

// What a mast-like tower carries says less than how it is built, so a dish,
// dome or lattice wins here.
const carriedTower = (type: PoiIconName, lattice: PoiIconName): IconNode => ({
  '*': type,
  'tower:construction': {
    '*': type,
    dish: 'tower_dish',
    dome: 'tower_dome',
    lattice,
  },
});

const towerByConstruction: IconNode = {
  dish: 'tower_dish',
  dome: 'tower_dome',
  lattice: 'tower_lattice',
};

// `power=generator` and `power=plant` classify their source the same way, under
// different tag keys — a plant being the site, a generator a single unit.
const powerSourceMapping: IconNode = {
  biofuel: 'temaki:plant',
  biogas: 'temaki:plant',
  biomass: 'temaki:plant',
  coal: 'industry',
  diesel: 'fuel',
  gas: 'temaki:gas',
  gasoline: 'fuel',
  geothermal: 'temaki:geyser_from_ground',
  hydro: 'dam',
  nuclear: 'temaki:radiation',
  oil: 'temaki:oil_well',
  solar: 'solar_panel',
  waste: 'temaki:waste',
  wind: 'generator_wind',
};

export const osmTagToIconMapping: IconNode = {
  'abandoned:man_made': {
    adit: 'disused_mine',
    mineshaft: 'disused_mine',
  },
  aerialway: {
    '*': 'aerialway',
  },
  aeroway: {
    aerodrome: 'aerodrome',
    helipad: 'helipad',
    terminal: 'terminal',
    apron: 'temaki:planes',
    gate: 'gate',
    hangar: 'temaki:hangar',
    holding_position: 'temaki:plane_taxiing',
    jet_bridge: 'temaki:pedestrian_walled',
    parking_position: 'temaki:plane_taxiing',
    taxiway: 'temaki:plane_taxiing',
    windsock: 'temaki:windsock',
    airstrip: 'aerodrome',
    navigationaid: 'antenna',
    runway: 'temaki:plane_taxiing',
    stopway: 'temaki:plane_taxiing',
    taxilane: 'temaki:plane_taxiing',
    threshold: 'temaki:plane_taxiing',
    tower: 'tower',
  },
  amenity: {
    animal_shelter: 'animal_shelter',
    atm: 'atm',
    arts_centre: 'arts_centre',
    bank: 'bank',
    bar: 'bar',
    bbq: 'bbq',
    bench: 'bench',
    bicycle_parking: 'bicycle_parking',
    bicycle_repair_station: 'bicycle_repair_station',
    bicycle_rental: 'bicycle_rental',
    biergarten: 'biergarten',
    boat_rental: 'boat_rental',
    bureau_de_change: 'bureau_de_change',
    bus_station: 'bus_station',
    cafe: 'cafe',
    car_rental: 'car_rental',
    car_wash: 'car_wash',
    casino: 'casino',
    charging_station: 'charging_station',
    cinema: 'cinema',
    clinic: 'doctors',
    college: 'college',
    community_centre: 'community_centre',
    courthouse: 'courthouse',
    dentist: 'dentist',
    doctors: 'doctors',
    drinking_water: 'drinking_water',
    embassy: 'embassy',
    emergency_phone: 'phone',
    excrement_bags: 'excrement_bags',
    fast_food: 'fast_food',
    feeding_place: 'manger',
    game_feeding: 'manger',
    ferry: 'ferry_terminal',
    fire_station: 'fire_station',
    fountain: 'fountain',
    fuel: 'fuel',
    grave_yard: 'cemetery',
    hospital: 'hospital',
    hunting_stand: 'hunting_stand',
    ice_cream: 'ice_cream',
    internet_cafe: 'internet_cafe',
    library: 'books',
    motorcycle_parking: 'motorcycle_parking',
    nightclub: 'nightclub',
    parking: 'parking',
    parking_entrance: 'parking',
    parking_space: 'parking',
    pharmacy: 'pharmacy',
    place_of_worship: {
      '*': 'place_of_worship',
      religion: {
        jewish: 'synagogue',
        muslim: 'mosque',
      },
      building: worshipBuildingMapping,
    },
    police: 'police',
    post_box: 'post_box',
    post_office: 'post_office',
    prison: 'prison',
    pub: 'pub',
    public_bath: 'public_bath',
    public_bookcase: 'public_bookcase',
    ranger_station: 'ranger-station',
    recycling: 'recycling',
    restaurant: 'restaurant',
    school: 'school',
    shelter: {
      '*': 'shelter',
      shelter_type: {
        basic_hut: 'basic_hut',
        lean_to: 'lean_to',
        picnic_shelter: 'picnic_shelter',
        public_transport: 'public_transport',
        weather_shelter: 'weather_shelter',
      },
    },
    shower: 'shower',
    ski_rental: 'skiing',
    social_facility: 'social_facility',
    taxi: 'taxi',
    telephone: 'telephone',
    theatre: 'theatre',
    toilets: 'toilets',
    townhall: 'townhall',
    university: 'university',
    vehicle_inspection: 'vehicle_inspection',
    vending_machine: {
      '*': 'vending_machine',
      vending: {
        parking_tickets: 'parking_tickets',
        public_transport_tickets: 'public_transport_tickets',
      },
    },
    veterinary: 'veterinary',
    waste_basket: 'waste_basket',
    waste_disposal: 'waste_disposal',
    watering_place: 'watering_place',
    animal_breeding: 'veterinary',
    clock: 'temaki:clock',
    device_charging_station: 'temaki:electronic',
    dressing_room: 'maki:clothing-store',
    driving_school: 'temaki:car_pool',
    ferry_terminal: 'ferry_terminal',
    funeral_hall: 'cemetery',
    kindergarten: 'kindergarten',
    language_school: 'school',
    marketplace: 'marketplace',
    monastery: 'place_of_worship',
    money_transfer: 'temaki:money_hand',
    motorcycle_rental: 'temaki:motorcycle_rental',
    parcel_locker: 'parcel_locker',
    planetarium: 'maki:globe',
    traffic_park: 'bicycle',
    vacuum_cleaner: 'temaki:vacuum_station',
    water_point: 'drinking_water',
    brothel: 'nightclub',
    childcare: 'kindergarten',
    compressed_air: 'temaki:gas',
    events_venue: 'community_centre',
    exhibition_centre: 'gallery',
    food_court: 'restaurant',
    gambling: 'casino',
    grit_bin: 'temaki:waste',
    letter_box: 'post_box',
    loading_dock: 'warehouse',
    lounger: 'bench',
    place_of_mourning: 'cemetery',
    public_building: 'building',
    smoking_area: 'tobacco',
    social_centre: 'community_centre',
    stripclub: 'nightclub',
    studio: 'music',
    trolley_bay: 'shop',
  },
  barrier: {
    cycle_barrier: 'cycle_barrier',
    entrance: 'entrance',
    fence: 'fence',
    gate: 'gate',
    hampshire_gate: 'gate',
    kissing_gate: 'kissing_gate',
    lift_gate: 'lift_gate',
    motorcycle_barrier: 'motorcycle_barrier',
    sally_port: 'gate',
    stile: 'stile',
    swing_gate: 'lift_gate',
    toll_booth: 'toll_booth',
    wicket_gate: 'gate',
    cattle_grid: 'cattle_grid',
    bollard: 'temaki:bollard',
    border_control: 'temaki:passport_checkpoint',
    cable_barrier: 'maki:roadblock',
    'full-height_turnstile': 'temaki:turnstile',
    jersey_barrier: 'maki:roadblock',
    chain: 'temaki:rope_fence',
    city_wall: 'temaki:wall',
    ditch: 'temaki:trench',
    guard_rail: 'temaki:guard_rail',
    handrail: 'temaki:railing',
    hedge: 'temaki:hedge',
    kerb: 'temaki:kerb-raised',
    retaining_wall: 'temaki:wall',
    rope: 'temaki:rope_fence',
    turnstile: 'temaki:turnstile',
    wall: 'temaki:wall',
    block: 'maki:roadblock',
    bus_trap: 'maki:roadblock',
    chicane: 'maki:roadblock',
    height_restrictor: 'maki:roadblock',
    log: 'obstacle_tree',
    planter: 'temaki:vase',
    sliding_gate: 'gate',
    spikes: 'maki:roadblock',
  },
  bridge: {
    '*': 'bridge',
  },
  boundary: {
    administrative: {
      admin_level: {
        6: 'city',
        9: 'village',
      },
    },
  },
  club: {
    automobile: 'temaki:car_pool',
    charity: 'charity',
    culture: 'arts_centre',
    fishing: 'fishing',
    hunting: 'hunting_stand',
    music: 'music',
    nature: 'park',
    scout: 'camp_site',
    social: 'community_centre',
    veterans: 'temaki:military',
  },
  craft: {
    // Anything not named below still reads as a workshop.
    '*': 'temaki:tools',
    bakery: 'bakery',
    beekeeper: 'beehive',
    blacksmith: 'temaki:anvil_and_hammer',
    brewery: 'temaki:storage_fermenter',
    builder: 'construction',
    caterer: 'temaki:catering',
    cleaning: 'temaki:vacuum',
    confectionery: 'maki:confectionery',
    distillery: 'temaki:storage_fermenter',
    dressmaker: 'temaki:dress',
    electrician: 'temaki:power',
    electronics_repair: 'electronics',
    gardener: 'maki:garden-centre',
    glaziery: 'temaki:window',
    handicraft: 'temaki:vase',
    hvac: 'temaki:tools',
    jeweller: 'jewelry',
    locksmith: 'temaki:lock',
    metal_construction: 'temaki:tools',
    painter: 'maki:paint',
    photographer: 'photo',
    plumber: 'temaki:plumber',
    pottery: 'temaki:vase',
    sawmill: 'maki:logging',
    shoemaker: 'temaki:hammer_shoe',
    stonemason: 'temaki:brick_trowel',
    tailor: 'temaki:needle_and_spool',
    tiler: 'temaki:brick_trowel',
    upholsterer: 'furniture',
    watchmaker: 'maki:watch',
    window_construction: 'temaki:window',
    winery: 'maki:alcohol-shop',
  },
  emergency: {
    defibrillator: 'defibrillator',
    phone: 'phone',
    access_point: 'danger',
    ambulance_station: 'hospital',
    assembly_point: 'danger',
    fire_extinguisher: 'fire_station',
    fire_hose: 'fire_station',
    fire_hydrant: 'fire_station',
    fire_water_pond: 'maki:water',
    landing_site: 'helipad',
    life_ring: 'maki:water',
    lifeguard: 'swimming_outdoor',
    siren: 'danger',
    suction_point: 'maki:water',
    water_tank: 'storage_tank',
  },
  entrance: {
    '*': 'entrance',
    // no: no_entrance, // TODO
  },
  fitness_station: {
    // One drawing for the whole outdoor gym, whatever the exercise.
    '*': 'fitness_station',
  },
  ford: {
    yes: 'ford',
  },
  healthcare: {
    // Anything not named below still reads as a doctor's office.
    '*': 'doctors',
    dentist: 'dentist',
    hospital: 'hospital',
    optometrist: 'optician',
    pharmacy: 'pharmacy',
    physiotherapist: 'massage',
    veterinary: 'veterinary',
  },
  highway: {
    bus_stop: 'bus_stop',
    elevator: 'elevator',
    ford: 'ford',
    rest_area: 'highway-rest-area',
    steps: 'stairs',
    traffic_signals: 'traffic_signals',
    bridleway: 'horse_riding',
    construction: 'construction',
    footway: 'temaki:pedestrian',
    raceway: 'temaki:speedway_oval',
    street_lamp: 'temaki:street_lamp_arm',
    // The road classes iD draws in perspective, so a trunk road reads as a
    // wider one than a lane. Link roads take their parent's drawing.
    motorway: 'id:highway-motorway',
    motorway_link: 'id:highway-motorway',
    trunk: 'id:highway-trunk',
    trunk_link: 'id:highway-trunk',
    primary: 'id:highway-primary',
    primary_link: 'id:highway-primary',
    secondary: 'id:highway-secondary',
    secondary_link: 'id:highway-secondary',
    tertiary: 'id:highway-tertiary',
    tertiary_link: 'id:highway-tertiary',
    unclassified: 'id:highway-unclassified',
    residential: 'id:highway-residential',
    living_street: 'id:highway-living-street',
    service: 'id:highway-service',
    // Nothing draws a track or a path as such, so they take the plain line.
    path: 'id:other-line',
    road: 'id:other-line',
    track: 'id:other-line',
    platform: 'temaki:board_transit',
    via_ferrata: 'climbing',
    busway: 'bus_station',
    corridor: 'temaki:pedestrian_walled',
    crossing: 'temaki:pedestrian',
    cycleway: 'bicycle',
    emergency_access_point: 'danger',
    ladder: 'stairs',
    milestone: 'boundary_stone',
    motorway_junction: 'id:highway-motorway',
    pedestrian: 'temaki:pedestrian_walled',
    piste: 'skiing',
    proposed: 'construction',
    services: 'highway-rest-area',
    speed_camera: 'temaki:security_camera',
    toll_gantry: 'toll',
    traffic_sign: 'board',
    trailhead: 'guidepost',
    emergency_bay: 'highway-rest-area',
    escape: 'danger',
    give_way: 'board',
    hitchhiking: 'temaki:pedestrian',
    priority: 'board',
    stop: 'board',
    traffic_calming: 'temaki:kerb-raised',
  },
  historic: {
    archaeological_site: 'archaeological_site',
    boundary_stone: 'boundary_stone',
    castle: {
      '*': 'castle',
      castle_type: {
        fortress: 'fortress',
        palace: 'palace',
      },
    },
    city_gate: 'city_gate',
    fort: 'fort',
    manor: 'manor',
    ruins: {
      '*': 'ruins',
      ruins: {
        castle: 'castle',
      },
    },
    memorial: {
      '*': 'memorial',
      memorial: {
        bust: 'bust',
        plaque: 'plaque',
        statue: 'statue',
        stone: 'memorial_stone',
      },
    },
    mine: 'disused_mine',
    mine_adit: 'disused_mine',
    mine_shaft: 'disused_mine',
    monument: 'monument',
    obelisk: 'obelisk',
    wayside_cross: 'cross',
    wayside_shrine: 'wayside_shrine',
    building: 'building',
    church: 'church',
    stone: 'stone',
    tomb: 'cemetery',
    aircraft: 'temaki:planes',
    battlefield: 'temaki:military',
    bomb_crater: 'sinkhole',
    bunker: 'bunker',
    cannon: 'temaki:military',
    charcoal_pile: 'firepit',
    citywalls: 'temaki:wall',
    dome: 'tower_dome',
    heritage: 'monument',
    hollow_way: 'id:other-line',
    house: 'building',
    milestone: 'boundary_stone',
    monastery: 'place_of_worship',
    quarry: 'temaki:pick_hammer',
    railway: 'rail',
    railway_station: 'station',
    roman_road: 'id:other-line',
    shieling: 'hut',
    tower: 'tower',
    tree_shrine: 'wayside_shrine',
    wreck: 'temaki:sailboat',
  },
  landuse: {
    garages: 'warehouse',
    cemetery: 'cemetery',
    commercial: 'commercial',
    construction: 'construction',
    farmland: 'farm',
    farmyard: 'farm',
    industrial: {
      '*': 'industry',
      industrial: 'mine',
    },
    logging: 'logging',
    residential: 'residential-community',
    allotments: 'garden_centre',
    basin: 'maki:water',
    brownfield: 'temaki:bulldozer',
    education: 'school',
    forest: 'maki:park-alt1',
    grass: 'temaki:lawn',
    greenfield: 'temaki:bulldozer',
    landfill: 'temaki:bulldozer',
    meadow: 'maki:garden',
    military: 'temaki:military',
    orchard: 'park',
    quarry: 'temaki:pick_hammer',
    railway: 'temaki:train',
    recreation_ground: 'running',
    reservoir: 'maki:water',
    retail: 'commercial',
    vineyard: 'temaki:grapes',
    animal_keeping: 'farm',
    apiary: 'beehive',
    aquaculture: 'fishing',
    churchyard: 'cemetery',
    civic: 'townhall',
    civic_admin: 'townhall',
    conservation: 'park',
    culture: 'arts_centre',
    depot: 'warehouse',
    emergency: 'fire_station',
    fairground: 'maki:amusement-park',
    farm: 'farm',
    flowerbed: 'florist',
    forestry: 'logging',
    garden: 'maki:garden',
    greenery: 'maki:garden',
    greenhouse_horticulture: 'garden_centre',
    harbour: 'temaki:sailboat',
    healthcare: 'hospital',
    highway: 'id:other-line',
    houseboat: 'temaki:sailboat',
    institutional: 'building',
    mine_spoils: 'temaki:bulldozer',
    paddock: 'horse_riding',
    peat_cutting: 'temaki:pick_hammer',
    pedestrian: 'temaki:pedestrian_walled',
    piste: 'skiing',
    plant_nursery: 'garden_centre',
    plantation: 'maki:park-alt1',
    religious: 'place_of_worship',
    salt_pond: 'maki:water',
    school: 'school',
    shrubs: 'temaki:shrub',
    static_caravan: 'caravan_site',
    storage: 'warehouse',
    tourism: 'attraction',
    tree_pit: 'temaki:tree_broadleaved',
    village_green: 'temaki:lawn',
    wasteland: 'temaki:bulldozer',
    winter_sports: 'skiing',
  },
  leisure: {
    bandstand: 'music',
    dance: 'dance',
    beach_resort: 'beach_resort',
    bird_hide: 'bird_hide',
    bowling_alley: 'bowling_alley',
    dog_park: 'dog_park',
    firepit: 'firepit',
    fishing: 'fishing',
    fitness_centre: 'fitness_centre',
    fitness_station: 'fitness_station',
    horse_riding: 'horse_riding',
    golf_course: 'golf_course',
    miniature_golf: 'miniature_golf',
    outdoor_seating: 'outdoor_seating',
    park: 'park',
    picnic_table: 'picnic_table',
    pitch: 'running',
    playground: 'playground',
    sauna: 'sauna',
    slipway: 'slipway',
    stadium: 'stadium',
    swimming_pool: 'water_park',
    swimming_area: 'swimming_outdoor',
    track: {
      '*': 'racetrack',
      sport: {
        cycling: 'racetrack-cycling',
        equestrian: 'racetrack-horse',
        horse_racing: 'racetrack-horse',
      },
    },
    water_park: 'water_park',
    adult_gaming_centre: 'casino',
    amusement_arcade: 'maki:gaming',
    bathing_place: 'maki:water',
    bleachers: 'temaki:bleachers',
    disc_golf_course: 'temaki:disc_golf_basket',
    garden: 'maki:garden',
    marina: 'temaki:sailboat',
    nature_reserve: 'park',
    resort: 'maki:lodging',
    sports_centre: 'running',
    sports_hall: 'running',
    common: 'park',
    escape_game: 'maki:gaming',
    hackerspace: 'computer',
    high_ropes_course: 'temaki:abseiling',
    hot_tub: 'public_bath',
    ice_rink: 'ice_skating',
    indoor_play: 'playground',
    recreation_ground: 'running',
    schoolyard: 'school',
    summer_camp: 'camp_site',
    sunbathing: 'beach',
    tanning_salon: 'beauty',
    trampoline_park: 'playground',
    wildlife_hide: 'bird_hide',
  },
  man_made: {
    adit: 'mine',
    antenna: 'antenna',
    apiary: 'beehive',
    beehive: 'beehive',
    bridge: 'bridge',
    chimney: 'chimney',
    cross: 'cross',
    clearcut: 'logging',
    dyke: 'dyke',
    embankment: 'embankment',
    foresters_lodge: "forester's_lodge",
    lighthouse: 'lighthouse',
    mast: {
      '*': 'mast',
      'mast:type': {
        communication: 'mast_communication',
        lighting: 'mast_lighting',
      },
    },
    mineshaft: 'mine',
    pipeline: 'id:pipeline-line',
    reservoir_covered: 'water_works',
    tower: {
      '*': 'tower',
      'tower:type': {
        '*': { '*': 'tower', 'tower:construction': towerByConstruction },
        bell_tower: towerOfType('tower_bell_tower'),
        cooling: towerOfType('tower_cooling'),
        defensive: towerOfType('tower_defensive'),
        observation: towerOfType('tower_observation'),
        watchtower: towerOfType('tower_observation'),
        communication: carriedTower(
          'tower_communication',
          'tower_lattice_communication',
        ),
        lighting: carriedTower('tower_lighting', 'tower_lattice_lighting'),
      },
      'tower:construction': towerByConstruction,
    },
    water_tower: 'water_tower',
    water_well: 'water_well',
    water_works: 'water_works',
    watermill: 'watermill',
    windmill: 'windmill',
    beacon: 'maki:communications-tower',
    bunker_silo: 'temaki:bunker_silo',
    communications_tower: 'communications_tower',
    cairn: 'temaki:cairn',
    cutline: 'logging',
    flagpole: 'embassy',
    "forester's_lodge": "forester's_lodge",
    gasometer: 'storage_tank',
    goods_conveyor: 'temaki:conveyor',
    manhole: 'temaki:manhole',
    monitoring_station: 'antenna',
    obelisk: 'obelisk',
    observatory: 'telescope_dome',
    pier: 'temaki:pier_fixed',
    pumping_station: 'temaki:powered_pump',
    silo: 'temaki:silo',
    storage_tank: 'storage_tank',
    surveillance: 'temaki:security_camera',
    // An untyped telescope is optical, as the renderer and Carto both take it.
    telescope: {
      '*': 'telescope_dome',
      'telescope:type': {
        optical: 'telescope_dome',
        radio: 'telescope_dish',
      },
    },
    survey_point: 'temaki:benchmark_disk',
    utility_pole: 'temaki:utility_pole',
    wastewater_plant: 'temaki:waste',
    water_tap: 'temaki:water_tap',
    works: 'industry',
    breakwater: 'dyke',
    cellar_entrance: 'entrance',
    cooling_tower: 'tower_cooling',
    crane: 'temaki:conveyor',
    groyne: 'dyke',
    nesting_site: 'bird_hide',
    quay: 'temaki:pier_fixed',
    snow_cannon: 'temaki:snow',
    spoil_heap: 'temaki:bulldozer',
    spring_box: 'spring',
    street_cabinet: 'temaki:electronic',
    tunnel: 'tunnel',
  },
  military: {
    '*': 'temaki:military',
    bunker: 'bunker',
    danger_area: 'danger',
    checkpoint: 'temaki:military_checkpoint',
    office: 'temaki:military',
    school: 'school',
    trench: 'temaki:trench',
  },
  mountain_pass: {
    yes: 'saddle',
  },
  genus: {
    '*': 'temaki:tree_broadleaved',
    Actinidia: 'temaki:shrub',
    Aronia: 'temaki:shrub',
    Berberis: 'temaki:shrub',
    Buxus: 'temaki:shrub',
    Camellia: 'temaki:shrub',
    Chaenomeles: 'temaki:shrub',
    Clematis: 'temaki:shrub',
    Cornus: 'temaki:shrub',
    Cotoneaster: 'temaki:shrub',
    Euonymus: 'temaki:shrub',
    Forsythia: 'temaki:shrub',
    Hedera: 'temaki:shrub',
    Hibiscus: 'temaki:shrub',
    Hippophae: 'temaki:shrub',
    Humulus: 'temaki:shrub',
    Hydrangea: 'temaki:shrub',
    Lavandula: 'temaki:shrub',
    Ligustrum: 'temaki:shrub',
    Lonicera: 'temaki:shrub',
    Mahonia: 'temaki:shrub',
    Nerium: 'temaki:shrub',
    Philadelphus: 'temaki:shrub',
    Pinus: {
      '*': 'temaki:tree_broadleaved',
      species: { 'Pinus mugo': 'temaki:shrub' },
    },
    Prunus: {
      species: {
        'Prunus laurocerasus': 'temaki:shrub',
        'Prunus spinosa': 'temaki:shrub',
      },
    },
    Pyracantha: 'temaki:shrub',
    Rhododendron: 'temaki:shrub',
    Ribes: 'temaki:shrub',
    Rosa: 'temaki:shrub',
    Rubus: 'temaki:shrub',
    Sambucus: 'temaki:shrub',
    Spiraea: 'temaki:shrub',
    Syringa: 'temaki:shrub',
    Vaccinium: 'temaki:shrub',
    Viburnum: 'temaki:shrub',
    Vitis: 'temaki:grapes',
    Weigela: 'temaki:shrub',
    Wisteria: 'temaki:shrub',
    Yucca: 'temaki:shrub',
  },
  natural: {
    arch: 'arch',
    beach: 'beach',
    cave_entrance: 'cave_entrance',
    earth_bank: 'earth_bank',
    gully: 'gully',
    hot_spring: 'hot_spring',
    peak: 'peak',
    rock: 'rock',
    saddle: 'saddle',
    sinkhole: 'sinkhole',
    spring: {
      '*': 'spring',
      drinking_water: {
        no: 'not_drinking_spring',
        yes: 'drinking_spring',
      },
      refitted: {
        yes: {
          '*': 'refitted_spring',
          drinking_water: {
            no: 'refitted_not_drinking_spring',
            yes: 'refitted_drinking_spring',
          },
        },
      },
    },
    stone: 'stone',
    tree: {
      '*': 'temaki:tree_broadleaved',
      denotation: {
        natural_monument: 'tree',
      },
      protected: {
        yes: 'tree_protected',
      },
    },
    volcano: 'volcano',
    wetland: 'wetland',
    bare_rock: 'temaki:boulder3',
    bay: 'beach',
    cliff: 'temaki:cliff_falling_rocks',
    geyser: 'temaki:geyser_from_ground',
    glacier: 'temaki:snow',
    grassland: 'temaki:grass',
    heath: 'temaki:shrub_low',
    ridge: 'temaki:mountain_range',
    scrub: 'temaki:shrub',
    shrub: 'temaki:shrub',
    tree_row: 'temaki:tree_row',
    valley: 'temaki:valley',
    water: {
      '*': 'maki:water',
      // Only the kinds drawn as something other than a plain body are listed;
      // a lake, pond or reservoir takes the drop above.
      water: {
        canal: 'id:waterway-canal',
        ditch: 'id:waterway-ditch',
        drain: 'id:waterway-ditch',
        fish_pass: 'temaki:fish_ladder',
        river: 'id:waterway-river',
        stream: 'id:waterway-stream',
        wastewater: 'temaki:waste',
      },
    },
    wood: 'maki:park-alt1',
    arete: 'temaki:mountain_range',
    basin: 'maki:water',
    cape: 'beach',
    crevasse: 'temaki:snow',
    dune: 'beach',
    grass: 'temaki:grass',
    hill: 'peak',
    isthmus: 'beach',
    landslide: 'temaki:cliff_falling_rocks',
    mountain_range: 'temaki:mountain_range',
    mud: 'wetland',
    peninsula: 'beach',
    reef: 'maki:water',
    sand: 'beach',
    scree: 'temaki:boulder3',
    shingle: 'temaki:boulder3',
    shoal: 'maki:water',
    shrubbery: 'temaki:shrub',
    strait: 'maki:water',
    tree_group: 'temaki:tree_row',
    tree_stump: 'obstacle_tree',
    fell: 'temaki:shrub_low',
    plain: 'temaki:grass',
    plateau: 'temaki:mountain_range',
  },
  obstacle: {
    '*': 'obstacle',
    vegetation: 'obstacle_vegetation',
    dense_vegetation: 'obstacle_vegetation',
    fallen_tree: 'obstacle_tree',
    fallen_trees: 'obstacle_tree',
    'fallen trees': 'obstacle_tree',
    deadwood: 'obstacle_tree',
    branches: 'obstacle_tree',
    tree: 'obstacle_tree',
    trees: 'obstacle_tree',
    root: 'obstacle_tree',
  },
  office: {
    // Anything not named below still reads as an office.
    '*': 'commercial',
    accountant: 'bank',
    architect: 'temaki:brick_trowel',
    association: 'community_centre',
    diplomatic: 'embassy',
    educational_institution: 'school',
    energy_supplier: 'temaki:power',
    estate_agent: 'maki:home',
    financial: 'bank',
    financial_advisor: 'bank',
    forestry: 'logging',
    government: 'townhall',
    insurance: 'temaki:money_hand',
    it: 'computer',
    lawyer: 'courthouse',
    logistics: 'warehouse',
    newspaper: 'newsagent',
    ngo: 'charity',
    notary: 'courthouse',
    political_party: 'townhall',
    property_management: 'maki:home',
    religion: 'place_of_worship',
    research: 'university',
    security: 'temaki:security_camera',
    tax_advisor: 'bank',
    telecommunication: 'communications_tower',
    travel_agent: 'travel_agency',
    union: 'community_centre',
    water_utility: 'water_works',
  },
  place: {
    city: 'city',
    town: 'town',
    farm: 'farm',
    village: 'village',
    hamlet: 'village',
    island: 'temaki:island_trees_building',
    islet: 'temaki:islet_tree',
    isolated_dwelling: 'maki:home',
    allotments: 'garden_centre',
    archipelago: 'temaki:island_trees_building',
    borough: 'residential-community',
    city_block: 'residential-community',
    house: 'maki:home',
    house_number: 'maki:home',
    neighbourhood: 'residential-community',
    ocean: 'maki:water',
    quarter: 'residential-community',
    sea: 'maki:water',
    square: 'temaki:pedestrian_walled',
    suburb: 'residential-community',
  },
  playground: {
    // Equipment reads as a playground unless it is also something else.
    '*': 'playground',
    climbing: 'climbing',
    climbing_pole: 'climbing',
    climbing_slope: 'climbing',
    climbingframe: 'climbing',
    climbingwall: 'climbing',
    exercise: 'fitness_station',
    horizontal_bar: 'fitness_station',
    sledding: 'temaki:sledding',
    splash_pad: 'water_park',
    teenshelter: 'shelter',
    water: 'water_park',
    water_channel: 'water_park',
    youth_bench: 'bench',
    zipwire: 'aerialway',
  },
  railway: {
    '*': 'rail',
    light_rail: 'rail-light',
    subway: 'rail-metro',
    station: 'station',
    halt: 'station',
  },
  route: {
    bicycle: 'bicycle',
    bus: 'bus_station',
    foot: 'temaki:pedestrian',
    hiking: 'temaki:pedestrian',
    horse: 'horse_riding',
    mtb: 'bicycle',
    piste: 'skiing',
    railway: 'rail',
    ski: 'skiing_crosscountry',
    tram: 'rail-light',
    aerialway: 'aerialway',
    canoe: 'temaki:canoe',
    canyoning: 'temaki:abseiling',
    detour: 'id:other-line',
    emergency_access: 'danger',
    ferry: 'ferry_terminal',
    fitness_trail: 'fitness_station',
    funicular: 'rail',
    historic: 'monument',
    historic_railway: 'rail',
    inline_skates: 'skateboard',
    light_rail: 'rail-light',
    motorboat: 'boat_rental',
    nordic_walking: 'temaki:pedestrian',
    pipeline: 'id:pipeline-line',
    portage: 'temaki:canoe',
    power: 'id:power-line',
    road: 'id:other-line',
    running: 'running',
    share_taxi: 'taxi',
    subway: 'rail-metro',
    tracks: 'rail',
    train: 'temaki:train',
    trolleybus: 'bus_station',
    via_ferrata: 'climbing',
    waterway: 'id:waterway-river',
    worship: 'cross',
  },
  ruins: {
    '*': 'ruins',
  },
  shop: {
    '*': 'shop',
    alcohol: 'alcohol',
    art: 'art',
    bag: 'bag',
    bakery: 'bakery',
    beauty: 'beauty',
    bed: 'bed',
    beverages: 'beverages',
    bicycle: 'bicycle',
    bookmaker: 'bookmaker',
    books: 'book',
    butcher: 'butcher',
    car_parts: 'car_parts',
    carpet: 'carpet',
    car_repair: 'car_repair',
    car: 'car',
    charity: 'charity',
    chemist: 'chemist',
    clothes: 'clothes',
    coffee: 'coffee',
    computer: 'computer',
    confectionery: 'confectionery',
    convenience: 'convenience',
    copyshop: 'copyshop',
    dairy: 'dairy',
    deli: 'deli',
    department_store: 'department_store',
    diy: 'doityourself',
    doityourself: 'doityourself',
    electronics: 'electronics',
    fabric: 'fabric',
    farm: 'greengrocer',
    florist: 'florist',
    furniture: 'furniture',
    garden_centre: 'garden_centre',
    gift: 'gift',
    greengrocer: 'greengrocer',
    hairdresser: 'hairdresser',
    hardware: 'hardware',
    hifi: 'hifi',
    houseware: 'houseware',
    ice_cream: 'ice_cream',
    interior_decoration: 'interior_decoration',
    jewelry: 'jewelry',
    laundry: 'laundry',
    marketplace: 'marketplace',
    massage: 'massage',
    medical_supply: 'medical_supply',
    mobile_phone: 'mobile_phone',
    musical_instrument: 'musical_instrument',
    music: 'music',
    newsagent: 'newsagent',
    optician: 'optician',
    outdoor: 'outdoor',
    paint: 'paint',
    perfumery: 'perfumery',
    pet: 'pet',
    photo: 'photo',
    seafood: 'seafood',
    second_hand: 'second_hand',
    shoes: 'shoes',
    sports: 'sports',
    stationery: 'stationery',
    supermarket: 'supermarket',
    tea: 'tea',
    ticket: 'ticket',
    tobacco: 'tobacco',
    toys: 'toys',
    trade: 'trade',
    travel_agency: 'travel_agency',
    tyres: 'tyres',
    variety_store: 'variety_store',
    video_games: 'video_games',
    video: 'video',
    watches: 'watch',
  },
  sport: {
    // Most of the sports OSM knows are played on a pitch of some kind and have
    // no icon anywhere; the generic one at least says what sort of place it is.
    '*': 'running',
    '8pin': 'bowling_alley',
    '9pin': 'bowling_alley',
    '10pin': 'bowling_alley',
    archery: 'archery',
    american_football: 'american_football',
    baseball: 'baseball',
    basketball: 'basketball',
    beachvolleyball: 'volleyball',
    bicycle: 'bicycle',
    bowling: 'bowling_alley',
    candlepin: 'bowling_alley',
    climbing: 'climbing',
    cricket: 'cricket',
    cycling: 'bicycle',
    diving: 'diving',
    equestrian: 'horse_riding',
    free_flying: 'free_flying',
    golf: 'golf_course',
    gym: 'gym',
    gymnastics: 'gymnasium',
    horse_racing: 'horse_riding',
    horse_riding: 'horse_riding',
    ice_skating: 'ice_skating',
    motor: 'motorracing',
    skateboard: 'skateboard',
    skating: 'ice_skating',
    ski_jumping: 'skiing',
    skiing: 'skiing',
    soccer: 'soccer',
    swimming: 'swimming_outdoor',
    table_tennis: 'table-tennis',
    tennis: 'tennis',
    volleyball: 'volleyball',
    american_handball: 'temaki:wall',
    athletics: 'running',
    australian_football: 'american_football',
    badminton: 'tennis',
    boules: 'running',
    bowls: 'running',
    climbing_adventure: 'temaki:abseiling',
    field_hockey: 'temaki:field_hockey',
    funnel_ball: 'running',
    futsal: 'soccer',
    gaga: 'running',
    handball: 'running',
    horseshoes: 'temaki:horseshoes',
    karting: 'temaki:speedway_8',
    miniature_golf: 'miniature_golf',
    netball: 'volleyball',
    padel: 'tennis',
    pickleball: 'temaki:pickleball',
    rugby_league: 'american_football',
    rugby_union: 'american_football',
    shuffleboard: 'temaki:shuffleboard',
    softball: 'baseball',
    table_soccer: 'temaki:table_soccer',
    tetherball: 'running',
    yoga: 'fitness_centre',
  },
  toll: {
    '*': 'toll',
  },
  tourism: {
    alpine_hut: 'alpine_hut',
    apartment: 'apartment',
    artwork: {
      '*': 'artwork',
      artwork_type: {
        bust: 'bust',
      },
    },
    attraction: 'attraction',
    camp_site: 'camp_site',
    chalet: 'chalet',
    guest_house: 'guest_house',
    hostel: 'hostel',
    hotel: 'hotel',
    information: {
      '*': 'information',
      information: {
        board: 'board',
        guidepost: 'guidepost',
        map: 'map',
        office: 'information_office',
      },
    },
    motel: 'motel',
    museum: 'museum',
    picnic_site: 'picnic_table',
    viewpoint: 'viewpoint',
    wilderness_hut: 'wilderness_hut',
    zoo: 'zoo',
    aquarium: 'maki:aquarium',
    caravan_site: 'caravan_site',
    gallery: 'gallery',
    theme_park: 'maki:amusement-park',
    cabin: 'chalet',
    camp_pitch: 'camp_site',
    hunting_lodge: 'hut',
  },
  tunnel: {
    '*': 'tunnel',
    // no: '', TODO
  },
  waterway: {
    dam: 'dam',
    waterfall: 'waterfall',
    fish_pass: 'temaki:fish_ladder',
    weir: 'weir',
    canal: 'id:waterway-canal',
    canoe_pass: 'temaki:canoe',
    ditch: 'id:waterway-ditch',
    drain: 'id:waterway-ditch',
    rapids: 'id:waterway-stream',
    river: 'id:waterway-river',
    stream: 'id:waterway-stream',
    tidal_channel: 'id:waterway-stream',
    boatyard: 'temaki:sailboat',
    dock: 'temaki:pier_fixed',
    drystream: 'id:waterway-stream',
    fairway: 'id:waterway-river',
    flowline: 'id:waterway-stream',
    link: 'id:waterway-stream',
    lock_gate: 'temaki:lock',
    milestone: 'boundary_stone',
    pressurised: 'id:pipeline-line',
    sluice_gate: 'weir',
    wadi: 'id:waterway-stream',
  },
  attraction: {
    amusement_ride: 'maki:amusement-park',
    animal: 'zoo',
    big_wheel: 'maki:amusement-park',
    carousel: 'temaki:amusement_park',
    maze: 'temaki:maze',
    roller_coaster: 'temaki:roller_coaster',
    summer_toboggan: 'temaki:sledding',
    train: 'temaki:train_kids',
    historic: 'monument',
    water_slide: 'water_park',
  },
  power: {
    generator: {
      '*': 'temaki:power',
      'generator:source': powerSourceMapping,
    },
    plant: {
      '*': 'industry',
      'plant:source': {
        ...powerSourceMapping,
        // A plant is a site, so it gets the cooling towers rather than the
        // trefoil a single reactor unit takes.
        nuclear: 'temaki:cooling_tower_radiation',
      },
    },
    // A line is the run of cable, not the pylon holding it up.
    line: 'id:power-line',
    minor_line: 'id:power-line',
    pole: 'temaki:power_pole',
    substation: 'temaki:power',
    tower: 'temaki:power_tower',
    transformer: 'temaki:power_transformer',
    cable: 'id:power-line',
    cable_distribution: 'temaki:power',
    cable_distribution_cabinet: 'temaki:electronic',
    catenary_mast: 'temaki:power_pole',
    catenary_portal: 'temaki:power_tower',
    compensator: 'temaki:power',
    connection: 'temaki:power_tower',
    converter: 'temaki:power',
    heliostat: 'solar_panel',
    insulator: 'temaki:power_tower',
    inverter: 'temaki:power',
    minor_cable: 'id:power-line',
    minor_underground_cable: 'id:power-line',
    portal: 'temaki:power_tower',
    roof_pole: 'temaki:power_pole',
    switch: 'temaki:power',
    switchgear: 'temaki:power',
    terminal: 'temaki:power_tower',
    transition: 'temaki:power_tower',
    circuit: 'id:power-line',
    line_section: 'id:power-line',
  },
  public_transport: {
    platform: 'temaki:board_transit',
    station: 'station',
    stop_position: 'temaki:transit',
  },

  // NOTE keep at the end
  building: buildingMapping,
  fixme: {
    '*': 'fixme',
  },
};
